import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Utensils } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { UNITS } from "../lib/constants";
import { servingCost } from "../lib/calc";
import { money } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

function ingCost(food, amount, unit) {
  if (!food) return null;
  const m = food.metrics || {};
  return servingCost(amount, unit, m.price, m.package_qty, m.package_unit, m.servings_on_package);
}

export default function MealBuilder({ open, onOpenChange, meal, foods, onSaved }) {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [name, setName] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (meal) {
        setName(meal.name);
        setRows(meal.ingredients.map((i) => ({ food_id: i.food_id, amount: i.amount, unit: i.unit })));
      } else { setName(""); setRows([]); }
    }
  }, [open, meal]);

  const foodById = (id) => foods.find((f) => f.id === id);

  const addRow = () => {
    const first = foods[0];
    if (!first) { toast.error("Add foods to your library first"); return; }
    setRows((r) => [...r, { food_id: first.id, amount: first.serving_amount || 1, unit: first.serving_unit || "oz" }]);
  };
  const setRow = (i, k, v) => setRows((r) => r.map((row, idx) => {
    if (idx !== i) return row;
    const next = { ...row, [k]: v };
    if (k === "food_id") {
      const f = foodById(v);
      if (f) { next.amount = f.serving_amount || 1; next.unit = f.serving_unit || "oz"; }
    }
    return next;
  }));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const total = rows.reduce((s, row) => {
    const c = ingCost(foodById(row.food_id), row.amount, row.unit);
    return s + (c || 0);
  }, 0);

  const save = async () => {
    if (!name) { toast.error("Name your meal"); return; }
    if (rows.length === 0) { toast.error("Add at least one ingredient"); return; }
    setSaving(true);
    const payload = { name, ingredients: rows.map((r) => ({ food_id: r.food_id, amount: parseFloat(r.amount), unit: r.unit })) };
    try {
      if (meal) await api.put(`/meals/${meal.id}`, payload);
      else await api.post("/meals", payload);
      toast.success(meal ? "Meal updated" : "Meal saved");
      onOpenChange(false); onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[520px]" data-testid="meal-builder-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{meal ? "Edit Meal" : "Build a Meal"}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Meal name</Label>
            <Input data-testid="meal-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Salmon Lunch" className="mt-1.5" autoFocus />
          </div>

          <div className="space-y-2">
            {rows.map((row, i) => {
              const f = foodById(row.food_id);
              const c = ingCost(f, row.amount, row.unit);
              return (
                <div key={i} data-testid={`meal-ingredient-row-${i}`} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5">
                  <div className="flex items-center gap-2">
                    <Select value={row.food_id} onValueChange={(v) => setRow(i, "food_id", v)}>
                      <SelectTrigger className="h-9 flex-1 bg-white" data-testid={`meal-food-select-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{foods.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => removeRow(i)} data-testid={`meal-remove-ingredient-${i}`}
                      className="rounded-lg p-1.5 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Input type="number" step="0.01" value={row.amount} onChange={(e) => setRow(i, "amount", e.target.value)}
                      className="h-9 w-24 bg-white" data-testid={`meal-amount-input-${i}`} />
                    <Select value={row.unit} onValueChange={(v) => setRow(i, "unit", v)}>
                      <SelectTrigger className="h-9 w-28 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="ml-auto font-mono text-sm font-semibold text-slate-700">{c != null ? money(c, cur) : "—"}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <Button variant="outline" onClick={addRow} data-testid="meal-builder-add-ingredient"
            className="w-full rounded-full border-dashed border-slate-300 text-slate-600"><Plus className="mr-1.5 h-4 w-4" />Add ingredient</Button>

          <div className="flex items-center justify-between rounded-2xl bg-brand-light p-4">
            <span className="font-display font-bold text-slate-700">Total Meal Cost</span>
            <span className="font-mono text-2xl font-extrabold text-brand" data-testid="meal-total-cost">{money(total, cur)}</span>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="meal-save-button"
            className="w-full rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Utensils className="mr-1.5 h-5 w-5" />{meal ? "Save changes" : "Save meal"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
